import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { importTickets, clearLastTicketImportLogs } from '../../redux';
import { Log, Setting } from '../../models';
import { Utils } from '../../services';
import i18n from '../../locales/i18n';
import classnames from 'classnames';

import './TicketsImporter.css';

const TicketsImporter = ({ visible }) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState('tickets');

    const {
        isImporting,
        hasImportingError,
        importingErrorMessage
    } = useSelector(state => state.tickets);

    const { setting }              = useSelector(state => state.settings);
    const { lastTicketImportLogs, isLoading } = useSelector(state => state.logs);

    const config = React.useMemo(() => setting ? (new Setting(setting)).getTicketsImportConfiguration() : null, [setting]);

    const toggle = tab => {
        if (activeTab !== tab)
            setActiveTab(tab);
    }

    const doImport = e => {
        dispatch(clearLastTicketImportLogs());
        dispatch(importTickets());
    }

    const isUseless = log => {
        if (!log )
            return false;

        return (
            log.inserted?.length === 0 &&
            log.updated?.length === 0 &&
            log.invalid?.length === 0
        );
    };

    const ticketsImportResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('tickets.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('tickets.import.nb_deleted', { count: log.nbDeleted }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            {isUseless(log) && (
                <div className="bg-info mt-2 p-2 rounded">
                    <b>
                        { t('tickets.import.was_useless') }
                    </b>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.inserted') }
                    </h5>
                    <ul>
                    {log.inserted.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <Link to={`/tickets/edit/${ticket._id}`}>
                                <b>
                                    {Utils.localized_or_fallback(ticket.title, i18n.language)}
                                </b>
                            </Link>
                            - { ticket.start_at.format('LLL') }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.updated') }
                    </h5>
                    <ul>
                    {log.updated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <Link to={`/tickets/edit/${ticket._id}`}>
                                <b>
                                    {Utils.localized_or_fallback(ticket.title, i18n.language)}
                                </b>
                            </Link>
                            - { ticket.start_at.format('LLL') }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('tickets.import.invalid') }
                    </h5>
                    {log.invalid.map((entry, i) => (
                        <div key={i} className="ticket">
                            {entry._id ? (
                                <Link to={`/tickets/edit/${entry._id}`}>
                                    <b>
                                        {Utils.localized_or_fallback(entry.title, i18n.language)}
                                    </b>
                                </Link>
                            ) : (
                                <b>
                                    {Utils.localized_or_fallback(entry.title, i18n.language)}
                                </b>
                            )}
                            - { entry.start_at.format('LLL') }
                            <ul>
                                {Object.keys(entry.invalid).map(key => (
                                    <li key={key}>
                                        <b>{ key }</b> : { entry.invalid[key] }
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), []);

    const ticketInfo = ticket => {
        const fullname = [
            ticket.contact?.firstname,
            ticket.contact?.lastname
        ].filter(Boolean).join(' ').trim();
        return [
            (ticket.type?.name || {})[i18n.language],
            fullname.length > 0 ? fullname : ticket.contact?.email
        ].map(s => s?.trim()).filter(Boolean).join(' - ');
    }
    const importResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('tickets.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('tickets.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_activated', { count: log.activated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            {(log.activated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.activated') }
                    </h5>
                    <ul>
                    {log.activated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.inserted') }
                    </h5>
                    <ul>
                    {log.inserted.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.updated') }
                    </h5>
                    <ul>
                    {log.updated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('tickets.import.invalid') }
                    </h5>
                    {log.invalid.map((ticket, i) => (
                        <div key={i} className="ticket">
                            {ticket._id ? (
                                <a target="_blank" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                    <b>
                                        { ticketInfo(ticket) }
                                    </b>
                                </a>
                            ) : (
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            )}
                            - { ticket._id }
                            <ul>
                                {Object.keys(ticket.invalid).map(key => (
                                    <li key={key}>
                                        <b>{ key }</b> : { ticket.invalid[key] }
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), [ setting ]);

    const ticketsImportLogs = lastTicketImportLogs?.filter(log => log.type === Log.TICKETS_IMPORT);

    if (!visible || !config)
        return null;

    return (
        <div className="TicketsImporter">
            <div className="mb-2">
                <small>
                    { t(`tickets.import.${config?.source}_import_desc`) }
                </small>
            </div>
            <Button onClick={ doImport } disabled={isImporting} color="info" title={t("tickets.import.do_import")} className={`TktSyncButton`}>
                <i className={`fa ${isImporting ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                <span>&nbsp;{ t('tickets.import.do_import') }</span>
            </Button>

            <hr />

            <div className="d-flex mt-2">
                {(isLoading || isImporting) ? (
                    <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                ) : (
                    <div className="mt-2 w-100">
                        {hasImportingError && (
                            <div className="mt-2 bg-danger p-2 rounded">
                                { importingErrorMessage || t('tickets.import.an_error_occured') }
                            </div>
                        )}

                        {(lastTicketImportLogs?.length > 0) && (
                        <>
                            <h4 className="text-center text-underline">
                                { t('tickets.import.imported_at', { date: lastTicketImportLogs[0].created_at.format('LLL') }) }
                            </h4>
                            <br />
                            { ticketsImportLogs?.map(log => importResume(log)) }
                        </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TicketsImporter;
